/**
 * DomainExporter - Exports the complete DDD structure to JSON.
 *
 * Creates a unified export containing:
 * - Domain hierarchy with ontology references
 * - Bounded contexts with their models
 * - Datasets (KnowledgeGraphs) with individuals and relationships
 * - Diagrams with layout information
 *
 * Each domain class provides its own toJSON() method with per-object @context.
 * The export format matches WorkspaceConfigRoot for consistency
 * between initial config, localStorage, and export/import.
 */

import type { Domain } from '@semantical/modules/semantical-core';
import type { DomainImpl } from '@semantical/modules/semantical-core/Domain';
import type { OntologyEnabledBoundedContext } from './OntologyEnabledBoundedContext';
import type KnowledgeGraph from '@semantical/modules/knowledge-graph/KnowledgeGraph';
import * as jsonld from 'jsonld';
import { Writer as N3Writer, Parser as N3Parser } from 'n3';

import type {
    WorkspaceConfigRoot,
    DomainConfig,
    BoundedContextConfig,
    ModelConfig,
    UbiquitousLanguageConfig,
    KnowledgeGraphDatasetConfig,
    ViewConfig,
    IndividualConfig,
    RelationshipConfig
} from './WorkspaceConfig';

/**
 * Export options for customizing the export.
 */
export interface ExportOptions {
    /** Include datasets (KnowledgeGraphs) in the export */
    includeDatasets?: boolean;

    /** Include diagrams in the export */
    includeDiagrams?: boolean;

    /** Pretty print the JSON output */
    prettyPrint?: boolean;
}

const DEFAULT_OPTIONS: ExportOptions = {
    includeDatasets: true,
    includeDiagrams: true,
    prettyPrint: true
};

/**
 * Export the complete DDD structure to a WorkspaceConfigRoot object.
 *
 * Uses toJSON() methods from domain classes for per-object @context.
 * Only injects runtime data (datasets, diagrams) for the active bounded context.
 *
 * @param rootDomains - The root domains
 * @param boundedContext - The active bounded context, optional
 * @param options - Export options
 * @returns The complete domain configuration
 */
export function exportDomainStructure(
    rootDomains: Domain[],
    boundedContext?: OntologyEnabledBoundedContext,
    options: ExportOptions = DEFAULT_OPTIONS
): WorkspaceConfigRoot {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    return {
        '@context': {
            '@vocab': 'http://semantical.io/ontology/core#',
            domains: { '@id': 'hasDomain', '@type': '@id', '@container': '@set' }
        },
        '@type': 'WorkspaceConfig',
        version: 1,
        domains: rootDomains.map(domain => exportDomain(domain, boundedContext, opts))
    };
}

/**
 * Export a domain and its children recursively.
 * Uses domain.toJSON() and injects bounded context data for the active context.
 */
function exportDomain(
    domain: Domain,
    boundedContext: OntologyEnabledBoundedContext | undefined,
    options: ExportOptions
): DomainConfig {
    // Use toJSON() if available (DomainImpl)
    if ('toJSON' in domain && typeof domain.toJSON === 'function') {
        const json = (domain as DomainImpl).toJSON();

        // Post-process to inject bounded context data into the active context
        if (boundedContext && (options.includeDatasets || options.includeDiagrams)) {
            json.contexts = json.contexts.map((ctx: unknown) => {
                const ctxObj = ctx as { '@id'?: string };
                if (ctxObj['@id'] === boundedContext.uri) {
                    return injectBoundedContextData(ctxObj as BoundedContextConfig, boundedContext, options);
                }
                return ctx;
            });

            // Also check subdomains recursively
            json.subdomains = json.subdomains.map((subdomain: unknown) => {
                return processSubdomainForBoundedContext(subdomain, boundedContext, options);
            });
        }

        return json as DomainConfig;
    }

    // Fallback for domains without toJSON()
    return {
        '@context': {
            '@vocab': 'http://semantical.io/ontology/core#',
            ontologies: { '@id': 'hasOntology', '@type': '@id', '@container': '@set' },
            contexts: { '@id': 'containsContext', '@type': '@id', '@container': '@set' },
            subdomains: { '@id': 'hasSubdomain', '@type': '@id', '@container': '@set' }
        },
        '@type': 'Domain',
        '@id': domain.uri,
        name: domain.name,
        definition: domain.definition,
        ontologies: domain.getOntologies().map(ont => ({
            '@type': 'Ontology',
            '@id': ont.namespace || ont.id,
            label: ont.label
        })),
        contexts: domain.getBoundedContexts().map(ctx =>
            exportBoundedContext(ctx, boundedContext, options)
        ),
        subdomains: domain.getChildren().map(child =>
            exportDomain(child, boundedContext, options)
        )
    } as DomainConfig;
}

/**
 * Recursively process subdomains to inject bounded context data.
 */
function processSubdomainForBoundedContext(
    subdomain: unknown,
    boundedContext: OntologyEnabledBoundedContext,
    options: ExportOptions
): unknown {
    const subObj = subdomain as { contexts?: unknown[]; subdomains?: unknown[] };

    if (subObj.contexts) {
        subObj.contexts = subObj.contexts.map((ctx: unknown) => {
            const ctxObj = ctx as { '@id'?: string };
            if (ctxObj['@id'] === boundedContext.uri) {
                return injectBoundedContextData(ctxObj as BoundedContextConfig, boundedContext, options);
            }
            return ctx;
        });
    }

    if (subObj.subdomains) {
        subObj.subdomains = subObj.subdomains.map((sub: unknown) =>
            processSubdomainForBoundedContext(sub, boundedContext, options)
        );
    }

    return subdomain;
}

/**
 * Inject datasets and views into a bounded context config.
 */
function injectBoundedContextData(
    config: BoundedContextConfig,
    boundedContext: OntologyEnabledBoundedContext,
    options: ExportOptions
): BoundedContextConfig {
    if (options.includeDatasets) {
        config.datasets = exportDatasets(boundedContext);
    }

    if (options.includeDiagrams) {
        config.views = exportViews(boundedContext);
    }

    return config;
}

/**
 * Export a bounded context with its datasets and diagrams.
 * Fallback for contexts without toJSON().
 */
function exportBoundedContext(
    context: import('@semantical/modules/semantical-core').BoundedContext,
    activeBoundedContext: OntologyEnabledBoundedContext | undefined,
    options: ExportOptions
): BoundedContextConfig {
    // Use toJSON() if available (OntologyEnabledBoundedContext)
    if ('toJSON' in context && typeof context.toJSON === 'function') {
        const json = (context as OntologyEnabledBoundedContext).toJSON({
            includeDiagrams: options.includeDiagrams,
            includeDatasets: options.includeDatasets
        });

        // Only include runtime data for the active bounded context
        const isActiveContext = activeBoundedContext?.id === context.id;
        if (!isActiveContext) {
            // Remove runtime data if not active context
            const config = { ...json } as BoundedContextConfig;
            delete config.datasets;
            delete config.views;
            return config;
        }

        // Add datasets and views for active bounded context
        const config = json as BoundedContextConfig;
        if (options.includeDatasets && activeBoundedContext) {
            config.datasets = exportDatasets(activeBoundedContext);
        }
        if (options.includeDiagrams && activeBoundedContext) {
            config.views = exportViews(activeBoundedContext);
        }

        return config;
    }

    // Fallback for contexts without toJSON()
    const isActiveContext = activeBoundedContext?.id === context.id;
    const model = context.getModel?.();
    const ubiquitousLanguage = context.getUbiquitousLanguage?.();

    const config: BoundedContextConfig = {
        '@context': {
            '@vocab': 'http://semantical.io/ontology/core#'
        },
        '@type': 'BoundedContext',
        '@id': context.uri,
        name: context.name,
        definition: context.definition,
        hasModel: model && 'toJSON' in model && typeof model.toJSON === 'function'
            ? (model as { toJSON: () => ModelConfig }).toJSON()
            : {
                '@context': { '@vocab': 'http://semantical.io/ontology/core#' },
                '@type': 'Model',
                '@id': `urn:semantical:model:${model?.id || context.id + '-model'}`,
                name: model?.name || `${context.name} Model`,
                hasSemanticLayer: {
                    '@context': {
                        '@vocab': 'http://semantical.io/ontology/core#',
                        includesOntology: { '@type': '@id', '@container': '@set' }
                    },
                    '@type': 'SemanticLayer',
                    includesOntology: []
                }
            }
    };

    // Add ubiquitous language if present
    if (ubiquitousLanguage && 'toJSON' in ubiquitousLanguage && typeof ubiquitousLanguage.toJSON === 'function') {
        const allVocabularies = ubiquitousLanguage.getAllVocabularies();
        if (allVocabularies.length > 0) {
            config.hasUbiquitousLanguage = (ubiquitousLanguage as { toJSON: () => UbiquitousLanguageConfig }).toJSON();
        }
    }

    // Only export datasets and views for the active bounded context
    if (isActiveContext && activeBoundedContext) {
        if (options.includeDatasets) {
            config.datasets = exportDatasets(activeBoundedContext);
        }
        if (options.includeDiagrams) {
            config.views = exportViews(activeBoundedContext);
        }
    }

    return config;
}

/**
 * Export all datasets (KnowledgeGraphs) from the bounded context.
 */
function exportDatasets(boundedContext: OntologyEnabledBoundedContext): KnowledgeGraphDatasetConfig[] {
    const datasets: KnowledgeGraphDatasetConfig[] = [];
    const viewIds = boundedContext.getAllViewIds();

    for (const viewId of viewIds) {
        const kg = boundedContext.getKnowledgeGraph(viewId);
        if (kg) {
            datasets.push(exportKnowledgeGraph(kg, viewId));
        }
    }

    return datasets;
}

/**
 * Export a KnowledgeGraph to config format.
 */
function exportKnowledgeGraph(kg: KnowledgeGraph, id: string): KnowledgeGraphDatasetConfig {
    const individuals: IndividualConfig[] = [];
    const relationships: RelationshipConfig[] = [];

    // Export individuals
    for (const individual of kg.individuals.values()) {
        // Convert propertyValues Map to object
        const dataProperties: Record<string, unknown> = {};
        if (individual.propertyValues) {
            individual.propertyValues.forEach((values, key) => {
                dataProperties[key] = values;
            });
        }

        individuals.push({
            uri: individual.uri,
            name: individual.name,
            classUri: individual.classUris[0] || '', // Use first class URI
            dataProperties: Object.keys(dataProperties).length > 0 ? dataProperties : undefined
        });
    }

    // Export relationships
    for (const rel of kg.relationships.values()) {
        relationships.push({
            id: rel.id,
            sourceUri: rel.subject.uri,
            targetUri: rel.object.uri,
            propertyUri: rel.predicateUri
        });
    }

    return {
        type: 'KnowledgeGraph',
        id: id,
        name: kg.name,
        namespace: kg.uri, // KnowledgeGraph uses 'uri' not 'namespace'
        individuals,
        relationships
    };
}

/**
 * Export all views from the bounded context.
 * ViewConfig only contains id, name, viewType - content is in DiagramRepository.
 */
function exportViews(boundedContext: OntologyEnabledBoundedContext): ViewConfig[] {
    return boundedContext.getViews().map(view => ({
        id: view.id,
        name: view.name,
        viewType: 'Diagram' as const  // Currently all views are Diagrams
    }));
}

/**
 * Export DDD structure to JSON string.
 */
export function exportDomainStructureToJson(
    rootDomains: Domain[],
    boundedContext?: OntologyEnabledBoundedContext,
    options: ExportOptions = DEFAULT_OPTIONS
): string {
    const config = exportDomainStructure(rootDomains, boundedContext, options);
    return options.prettyPrint
        ? JSON.stringify(config, null, 2)
        : JSON.stringify(config);
}

/**
 * Trigger download of DDD structure export as JSON file.
 */
export function downloadDomainExport(
    rootDomains: Domain[],
    boundedContext?: OntologyEnabledBoundedContext,
    options: ExportOptions = DEFAULT_OPTIONS
): void {
    const json = exportDomainStructureToJson(rootDomains, boundedContext, options);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domain-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Convert JSON-LD structure to Turtle format using jsonld and N3.js.
 *
 * @param jsonLdDoc - The JSON-LD document to convert
 * @returns Promise resolving to Turtle string
 */
export async function jsonLdToTurtle(jsonLdDoc: object): Promise<string> {
    // Convert JSON-LD to N-Quads format
    const nquads = await jsonld.toRDF(jsonLdDoc, { format: 'application/n-quads' });

    // Parse N-Quads using N3Parser and write as Turtle using N3Writer
    return new Promise((resolve, reject) => {
        const parser = new N3Parser({ format: 'N-Quads' });
        const writer = new N3Writer({
            prefixes: {
                '': 'http://semantical.io/ontology/core#',
                'sem': 'http://semantical.io/ontology/core#',
                'rdf': 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
                'rdfs': 'http://www.w3.org/2000/01/rdf-schema#',
                'owl': 'http://www.w3.org/2002/07/owl#',
                'xsd': 'http://www.w3.org/2001/XMLSchema#'
            }
        });

        // Parse the N-Quads string and add each quad to the writer
        const quads = parser.parse(nquads as string);
        for (const quad of quads) {
            writer.addQuad(quad);
        }

        writer.end((error, result) => {
            if (error) reject(error);
            else resolve(result);
        });
    });
}

/**
 * Export DDD structure to Turtle string.
 */
export async function exportDomainStructureToTurtle(
    rootDomains: Domain[],
    boundedContext?: OntologyEnabledBoundedContext,
    options: ExportOptions = DEFAULT_OPTIONS
): Promise<string> {
    const config = exportDomainStructure(rootDomains, boundedContext, options);
    return jsonLdToTurtle(config);
}

/**
 * Trigger download of DDD structure export as Turtle file.
 */
export async function downloadDomainExportAsTurtle(
    rootDomains: Domain[],
    boundedContext?: OntologyEnabledBoundedContext,
    options: ExportOptions = DEFAULT_OPTIONS
): Promise<void> {
    const turtle = await exportDomainStructureToTurtle(rootDomains, boundedContext, options);
    const blob = new Blob([turtle], { type: 'text/turtle;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domain-export-${new Date().toISOString().split('T')[0]}.ttl`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
